import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateTime, money, STAGES } from "@/lib/api";
import { AlertTriangle, Clock, FlaskConical, Layers, Send, TrendingUp, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";
import DailyBriefing from "@/components/DailyBriefing";

const Metric = ({ label, value, sub, icon: Icon, tone = "slate", testid }) => (
  <div className="border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <Icon size={15} className={tone === "danger" ? "text-red-800" : "text-[#134e4a]"} />
    </div>
    <div className={`mt-2 num font-display text-3xl font-bold ${tone === "danger" ? "text-red-800" : "text-slate-900"}`}>
      {value}
    </div>
    {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { can, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [work, setWork] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/cases", { params: { limit: 300 } }).then((r) => setCases(r.data)).catch(() => setCases([]));
    if (can("signout")) api.get("/worklist/summary").then((r) => setWork(r.data)).catch(() => setWork(null));
  }, [can]);

  const lastSync = useLivePoll(async () => {
    const [s, c] = await Promise.all([api.get("/dashboard/stats"), api.get("/cases", { params: { limit: 300 } })]);
    setStats(s.data);
    setCases(c.data);
    if (can("signout")) api.get("/worklist/summary").then((r) => setWork(r.data)).catch(() => {});
  }, 5000);

  if (!stats) return <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;

  const maxWork = Math.max(1, ...stats.workload.map((w) => w.cases));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <DailyBriefing userId={user?.id} enabled={!!user?.briefing_enabled} />
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Lab Dashboard</h1>
        <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">Live pipeline, turnaround and send-out status. <LiveDot lastSync={lastSync} testid="dashboard-live-indicator" /></p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Metric label="Active cases" value={stats.active_cases} sub={`${stats.total_cases} total`} icon={FlaskConical} testid="metric-active-cases" />
        <Metric label="STAT queue" value={stats.stat_count} sub="Awaiting sign-out" icon={AlertTriangle} tone={stats.stat_count ? "danger" : "slate"} testid="metric-stat-count" />
        <Metric label="Avg TAT" value={`${stats.avg_tat_hours}h`} sub={`Target ${stats.tat_target_hours}h`} icon={Clock} testid="metric-avg-tat" />
        <Metric label="TAT breaches" value={stats.tat_breaches} sub="Over target, open" icon={TrendingUp} tone={stats.tat_breaches ? "danger" : "slate"} testid="metric-tat-breaches" />
        <Metric label="Blocks & slides" value={stats.specimens.total} sub={`${stats.specimens.checked_out} checked out`} icon={Layers} testid="metric-specimens" />
        <Metric label="Send-outs" value={stats.sendouts.total} sub={`${stats.sendouts.pending_results} awaiting results`} icon={Send} testid="metric-sendouts" />
      </div>

      {work && (
        <Link to="/my-worklist" className="block border border-[#134e4a]/30 bg-[#134e4a]/5 p-4 transition-colors hover:bg-[#134e4a]/10" data-testid="dashboard-my-worklist-card">
          <div className="flex flex-wrap items-center gap-3">
            <Stethoscope size={18} className="text-[#134e4a]" />
            <span className="font-display text-lg font-bold text-slate-900">My Worklist</span>
            <span className="num text-sm text-slate-700" data-testid="dashboard-worklist-counts">
              {work.ready} ready to read · {work.stat} STAT · {work.overdue} over {work.tat_target_hours}h · {work.unassigned} unassigned
            </span>
            <span className="ml-auto text-sm font-semibold text-[#134e4a]">Open worklist →</span>
          </div>
        </Link>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="border border-slate-200 lg:col-span-2" data-testid="sendout-billing-summary">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Send-out billing & material return
          </div>
          <div className="grid grid-cols-2 divide-slate-200 sm:grid-cols-4 sm:divide-x">
            {[
              ["Unbilled", stats.sendouts.unbilled, "sendout-unbilled"],
              ["Charged to patient", stats.sendouts.charged, "sendout-charged"],
              ["Materials outstanding", stats.sendouts.pending_materials, "sendout-materials-pending"],
              ["Charges logged", money(stats.sendouts.charge_total), "sendout-charge-total"],
            ].map(([label, value, tid]) => (
              <div key={label} className="p-4" data-testid={tid}>
                <div className="text-xs text-slate-500">{label}</div>
                <div className="num mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-slate-200" data-testid="workload-panel">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Workload by pathologist
          </div>
          <div className="space-y-2 p-4">
            {stats.workload.length === 0 && <div className="text-sm text-slate-500">No assignments yet.</div>}
            {stats.workload.map((w) => (
              <div key={w.name}>
                <div className="flex justify-between text-xs text-slate-700">
                  <span>{w.name}</span><span className="num font-semibold">{w.cases}</span>
                </div>
                <div className="mt-1 h-1.5 bg-slate-100">
                  <div className="h-1.5 bg-[#134e4a]" style={{ width: `${(w.cases / maxWork) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-slate-200" data-testid="kanban-board">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Pipeline
        </div>
        <div className="grid gap-px bg-slate-200 md:grid-cols-3 xl:grid-cols-6">
          {STAGES.map((stage) => {
            const list = cases.filter((c) => c.status === stage);
            return (
              <div key={stage} className="bg-white p-3" data-testid={`kanban-column-${stage}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">{stage}</span>
                  <span className="num rounded bg-slate-100 px-1.5 text-xs font-semibold text-slate-700">{list.length}</span>
                </div>
                <div className="space-y-1.5">
                  {list.slice(0, 8).map((c) => (
                    <Link
                      key={c.id} to={`/cases/${c.id}`} data-testid={`kanban-card-${c.accession_number}`}
                      className="block border border-slate-200 p-2 row-hover hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs font-semibold text-[#134e4a]">{c.accession_number}</span>
                        {c.priority === "STAT" && <span className="bg-red-800 px-1 text-[10px] font-bold text-white">STAT</span>}
                      </div>
                      <div className="truncate text-xs text-slate-700">{c.patient_name}</div>
                      <div className="num text-[11px] text-slate-500">{c.specimen_type} · {c.tat_hours}h</div>
                    </Link>
                  ))}
                  {list.length === 0 && <div className="py-2 text-xs text-slate-400">Empty</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-slate-200" data-testid="stat-queue-panel">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
          STAT queue
        </div>
        {stats.stat_queue.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">No STAT cases pending.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-white text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-2">Accession</th><th className="px-4 py-2">Patient</th>
                <th className="px-4 py-2">Status</th><th className="px-4 py-2">Received</th>
                <th className="px-4 py-2 text-right">TAT</th>
              </tr>
            </thead>
            <tbody>
              {stats.stat_queue.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`stat-row-${c.accession_number}`}>
                  <td className="px-4 py-2"><Link className="font-mono font-semibold text-[#134e4a] hover:underline" to={`/cases/${c.id}`}>{c.accession_number}</Link></td>
                  <td className="px-4 py-2">{c.patient_name}</td>
                  <td className="px-4 py-2">{c.status}</td>
                  <td className="px-4 py-2 num text-slate-600">{fmtDateTime(c.received_at)}</td>
                  <td className="px-4 py-2 num text-right font-semibold">{c.tat_hours}h</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
